import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronRight, MessageSquareQuote, ImageIcon } from 'lucide-react';
import { ProductReview } from '../../types';
import { reviewService } from '../../services/ReviewService';
import { ReviewLightboxModal } from './ReviewLightboxModal';

interface GlobalReviewCarouselProps {
    onViewAll: () => void;
}

export const GlobalReviewCarousel: React.FC<GlobalReviewCarouselProps> = ({ onViewAll }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [reviews, setReviews] = useState<ProductReview[]>([]);
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

    useEffect(() => {
        reviewService.getAllApprovedReviews().then(data => {
            // Tomamos solo las últimas 10 para el carrusel
            setReviews(data.slice(0, 10));
        });
    }, []);

    if (!reviews || reviews.length === 0) return null;

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-SV', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <section className="py-16 md:py-24 bg-black border-y border-zinc-900 relative overflow-hidden">
            {/* Background Texture / Gradient */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-zinc-900/20 via-black to-black pointer-events-none" />

            <div className="container mx-auto px-4 relative z-10 mb-10 md:mb-16">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <MessageSquareQuote className="text-zinc-500" size={24} />
                            <h2 className="text-xs font-mono text-zinc-500 uppercase tracking-widest">Lo que dicen de nosotros</h2>
                        </div>
                        <h3 className="text-3xl md:text-5xl font-display font-black uppercase italic text-white leading-none">
                            Confianza <br className="md:hidden" />Verificada
                        </h3>
                    </div>
                    
                    <button 
                        onClick={onViewAll}
                        className="hidden md:flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-white transition-colors group"
                    >
                        Ver Todas las Reseñas
                        <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </div>

            {/* Carousel Container */}
            <div className="relative z-10">
                <div 
                    ref={scrollContainerRef}
                    className="flex overflow-x-auto snap-x snap-mandatory gap-4 px-4 md:px-10 pb-8 hide-scrollbar"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {reviews.map((review, index) => {
                        const hasImage = review.media && review.media.length > 0;
                        
                        return (
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                key={review.id}
                                onClick={() => setLightboxIndex(index)}
                                className="snap-center sm:snap-start shrink-0 w-[85vw] sm:w-[400px] bg-zinc-900/40 backdrop-blur-sm border border-zinc-800 p-6 flex flex-col justify-between cursor-pointer hover:border-zinc-500 transition-colors relative group"
                            >
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex gap-1">
                                            {[...Array(5)].map((_, i) => (
                                                <Star 
                                                    key={i} 
                                                    size={14} 
                                                    className={i < review.rating ? "fill-white text-white" : "text-zinc-700"} 
                                                />
                                            ))}
                                        </div>
                                        <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                                            {formatDate(review.created_at)}
                                        </span>
                                    </div>
                                    
                                    <div className="flex gap-4">
                                        {hasImage && (
                                            <div className="w-20 h-20 shrink-0 bg-zinc-800 border border-zinc-700 overflow-hidden">
                                                <img 
                                                    src={review.media![0].media_url} 
                                                    alt="Review" 
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                />
                                            </div>
                                        )}
                                        <p className={`text-sm md:text-base text-zinc-300 leading-relaxed mb-6 line-clamp-3 ${!hasImage ? 'w-full' : ''}`}>
                                            "{review.comment}"
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-zinc-800 flex items-center justify-center font-bold text-xs text-zinc-400">
                                        {review.customer_name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-white uppercase tracking-wider">{review.customer_name}</p>
                                        <p className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest flex items-center gap-1">
                                            <span className="w-1.5 h-1.5 rounded-full bg-white opacity-50" />
                                            Compra Verificada
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* Mobile View All Button */}
            <div className="md:hidden px-4 mt-4">
                <button 
                    onClick={onViewAll}
                    className="w-full py-4 bg-zinc-900 border border-zinc-800 text-xs font-bold uppercase tracking-widest text-zinc-300 flex items-center justify-center gap-2 active:bg-zinc-800"
                >
                    Ver Todas las Reseñas
                    <ChevronRight size={16} />
                </button>
            </div>

            {/* Lightbox Modal */}
            <ReviewLightboxModal 
                isOpen={lightboxIndex !== null}
                reviews={reviews}
                initialIndex={lightboxIndex || 0}
                onClose={() => setLightboxIndex(null)}
            />
        </section>
    );
};
